import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  authConfigured,
  beginSignIn,
  clearSession,
  getValidAccessToken,
  getUserProfile,
  loadSession,
  refreshSession,
  saveSession,
  signOut as redirectSignOut,
  type AuthSession,
  type UserProfile,
} from './cognito';

export interface AuthContextValue {
  readonly configured: boolean;
  readonly session: AuthSession | null;
  readonly user: UserProfile | null;
  readonly signedIn: boolean;
  readonly setSession: (session: AuthSession | null) => void;
  readonly refreshUser: () => Promise<void>;
  readonly signIn: () => Promise<void>;
  readonly signOut: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setStoredSession] = useState<AuthSession | null>(() => loadSession());
  const [serverUser, setServerUser] = useState<UserProfile | null>(null);
  const [warning, setWarning] = useState<{ reason: 'idle' | 'absolute'; remainingSeconds: number } | null>(null);
  const lastServerUserRefreshAt = useRef(0);
  const lastActivityAt = useRef(Date.now());
  const warningDeadlineAt = useRef(0);
  const warningReason = useRef<'idle' | 'absolute' | null>(null);

  const setSession = useCallback((nextSession: AuthSession | null) => {
    if (!nextSession) clearSession();
    setStoredSession(nextSession);
    if (!nextSession) setServerUser(null);
  }, []);

  const localUser = useMemo(() => getUserProfile(session), [session]);

  const signOut = useCallback(() => {
    setServerUser(null);
    redirectSignOut();
  }, []);

  const startSessionWarning = useCallback((reason: 'idle' | 'absolute') => {
    if (warningDeadlineAt.current) return;
    warningDeadlineAt.current = Date.now() + warningDurationMs;
    warningReason.current = reason;
    setWarning({ reason, remainingSeconds: Math.ceil(warningDurationMs / 1000) });
  }, []);

  const refreshUser = useCallback(async () => {
    if (!session || !authConfigured) return;
    lastServerUserRefreshAt.current = Date.now();
    const token = await getValidAccessToken();
    if (!token) {
      signOut();
      return;
    }

    const response = await fetch(`${apiBaseUrl}/v1/auth/me`, { headers: { authorization: `Bearer ${token}` } });
    if (response.status === 401 || response.status === 403) {
      signOut();
      return;
    }
    if (!response.ok) return;

    const payload = (await response.json()) as { userId?: string; username?: string; name?: string; roles?: string[] };
    setServerUser({
      email: localUser?.email,
      name: payload.name ?? localUser?.name,
      username: payload.username ?? localUser?.username,
      groups: payload.roles ?? localUser?.groups ?? [],
    });
  }, [localUser?.email, localUser?.groups, localUser?.name, localUser?.username, session, signOut]);

  useEffect(() => {
    if (!session) return;
    void refreshUser();
  }, [refreshUser, session]);

  useEffect(() => {
    if (!session) return;
    const onFocus = () => {
      if (Date.now() - lastServerUserRefreshAt.current > serverUserRefreshCooldownMs) void refreshUser();
    };
    window.addEventListener('focus', onFocus);
    return () => {
      window.removeEventListener('focus', onFocus);
    };
  }, [refreshUser, session]);

  useEffect(() => {
    if (!session) return;
    const sessionStartedAt = session.createdAt ?? Date.now();
    let intervalId = window.setInterval(() => {
      const now = Date.now();
      const absoluteExpired = now - sessionStartedAt >= absoluteSessionMs;
      const idleWarningDue = now - lastActivityAt.current >= idleLogoutMs - warningDurationMs;
      const absoluteWarningDue = now - sessionStartedAt >= absoluteSessionMs - warningDurationMs;
      if (!warningDeadlineAt.current && (idleWarningDue || absoluteWarningDue)) {
        startSessionWarning(absoluteWarningDue ? 'absolute' : 'idle');
      }
      if (warningDeadlineAt.current) {
        const remainingSeconds = Math.max(0, Math.ceil((warningDeadlineAt.current - now) / 1000));
        setWarning((current) => current ? { ...current, remainingSeconds } : current);
        if (remainingSeconds <= 0) signOut();
      }
      if (absoluteExpired) signOut();
    }, 1000);

    const resetIdleTimer = () => {
      if (warningDeadlineAt.current) return;
      lastActivityAt.current = Date.now();
    };
    idleEvents.forEach((eventName) => window.addEventListener(eventName, resetIdleTimer, { passive: true }));
    return () => {
      window.clearInterval(intervalId);
      idleEvents.forEach((eventName) => window.removeEventListener(eventName, resetIdleTimer));
    };
  }, [session, signOut, startSessionWarning]);

  useEffect(() => {
    if (!session || !hasAuthTimeoutTestParam()) return;
    const timeoutId = window.setTimeout(() => startSessionWarning('idle'), 1000);
    return () => window.clearTimeout(timeoutId);
  }, [session, startSessionWarning]);

  const continueSession = useCallback(async () => {
    if (!session?.refreshToken) {
      signOut();
      return;
    }
    try {
      const refreshed = await refreshSession(session.refreshToken);
      const continuedSession = { ...refreshed, createdAt: Date.now() };
      saveSession(continuedSession);
      lastActivityAt.current = Date.now();
      warningDeadlineAt.current = 0;
      warningReason.current = null;
      setStoredSession(continuedSession);
      setWarning(null);
      await refreshUser();
    } catch {
      signOut();
    }
  }, [refreshUser, session?.refreshToken, signOut]);

  const value = useMemo<AuthContextValue>(
    () => ({
      configured: authConfigured,
      session,
      user: serverUser ?? localUser,
      signedIn: Boolean(session),
      setSession,
      refreshUser,
      signIn: beginSignIn,
      signOut,
    }),
    [localUser, refreshUser, serverUser, session, setSession, signOut],
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
      {warning ? <SessionWarningDialog warning={warning} onContinue={continueSession} onSignOut={signOut} /> : null}
    </AuthContext.Provider>
  );
}

const apiBaseUrl = import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:3000';
const serverUserRefreshCooldownMs = Number(import.meta.env.VITE_AUTH_ME_REFRESH_COOLDOWN_MS ?? 15 * 60_000);
const idleLogoutMs = Number(import.meta.env.VITE_IDLE_LOGOUT_MS ?? 20 * 60_000);
const absoluteSessionMs = Number(import.meta.env.VITE_ABSOLUTE_SESSION_MS ?? 8 * 60 * 60_000);
const warningDurationMs = Number(import.meta.env.VITE_SESSION_WARNING_MS ?? 2 * 60_000);
const idleEvents = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll'] as const;

function hasAuthTimeoutTestParam() {
  if (typeof window === 'undefined') return false;
  return new URLSearchParams(window.location.search).get('test-unauth') === '1';
}

function SessionWarningDialog({ warning, onContinue, onSignOut }: { readonly warning: { readonly reason: 'idle' | 'absolute'; readonly remainingSeconds: number }; readonly onContinue: () => void; readonly onSignOut: () => void }) {
  return (
    <div className="fixed inset-0 z-[100] grid place-items-center bg-slate-950/40 p-4" role="dialog" aria-modal="true">
      <div className="w-full max-w-md rounded-md border border-[#d6ded0] bg-white p-5 shadow-xl">
        <h2 className="text-lg font-semibold text-[#223126]">Tu sesion esta por cerrarse</h2>
        <p className="mt-2 text-sm leading-6 text-[#5f6b58]">
          {warning.reason === 'absolute' ? 'La sesion alcanzo el limite maximo de 8 horas.' : 'Detectamos inactividad por casi 20 minutos.'}
          {' '}Se cerrara en {warning.remainingSeconds} segundo(s).
        </p>
        <div className="mt-5 flex flex-wrap justify-end gap-2">
          <button type="button" className="h-9 rounded-md border border-[#d6ded0] bg-white px-3 text-sm font-medium text-[#334033] hover:bg-[#eef3ea]" onClick={onSignOut}>Cerrar sesion</button>
          <button type="button" className="h-9 rounded-md bg-[#334033] px-3 text-sm font-medium text-white hover:bg-[#223126]" onClick={onContinue}>Continuar</button>
        </div>
      </div>
    </div>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside AuthProvider.');
  return context;
}
